import { useMemo } from "react";
import { Box, Flex, Heading, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from "@chakra-ui/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import KpiCard from "./KpiCard";
import PaginatedIndicatorGrid from "./PaginatedIndicatorGrid";
import { CATEGORICAL, COLORS } from "../../theme/dashboardTokens";
import { formatGriValue } from "./griFormat";
import { ordenarSeries, rotuloDaSerie } from "../../pages/gri-indicators/series";

// GRI-05 (CA04) — mesmo formato do dashboard GRI do respondente (GRI-04),
// portado pro admin: 1 empresa, indicadores por série, radar de cobertura.
// Indicadores GRI têm unidade incompatível entre si (tCO2e, %, R$, m³...) —
// nenhum gráfico soma valor bruto; "cobertura" (% do catálogo com dado no
// ano) é a única base comparável entre séries/anos (mesma decisão do GRI-04).

function yearColor(index) {
  return CATEGORICAL[index % CATEGORICAL.length];
}

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <Box bg={COLORS.surface} border="1px solid #E2E8F0" borderRadius="8px" p="10px" boxShadow="sm">
      <Text fontSize="13px" fontWeight={600} color={COLORS.ink} mb="4px">
        {label}
      </Text>
      {payload.map((p) => (
        <Text key={p.name} fontSize="12px" color={COLORS.inkMuted}>
          {p.name}: <b>{formatGriValue(p.value, unit)}</b>
        </Text>
      ))}
    </Box>
  );
}

export default function GriCompanyDashboard({ entries, catalogTotals, selectedYears }) {
  // Séries que a empresa realmente tem, não uma lista fixa de 4 — uma série
  // nova ganha aba sozinha assim que tiver dado, sem precisar mexer aqui.
  const seriesDisponiveis = useMemo(
    () => ordenarSeries([...new Set((entries || []).map((e) => e.series))]),
    [entries]
  );

  const bySeries = useMemo(() => {
    const grouped = new Map();
    seriesDisponiveis.forEach((s) => grouped.set(s, new Map()));
    (entries || []).forEach((entry) => {
      if (!selectedYears.includes(entry.year)) return;
      const bucket = grouped.get(entry.series);
      if (!bucket) return;
      if (!bucket.has(entry.id_gri_indicator)) {
        bucket.set(entry.id_gri_indicator, { code: entry.code, name: entry.name, unit: entry.unit, byYear: {} });
      }
      const numeric = Number(entry.value);
      bucket.get(entry.id_gri_indicator).byYear[entry.year] = Number.isFinite(numeric) ? numeric : entry.value;
    });
    return grouped;
  }, [entries, selectedYears, seriesDisponiveis]);

  if (!entries || entries.length === 0) {
    return (
      <Flex bg={COLORS.surface} borderRadius="12px" p={["32px", "48px"]} direction="column" align="center" textAlign="center" gap="8px">
        <Heading size="sm" color={COLORS.ink}>
          Sem dados GRI ainda
        </Heading>
        <Text color={COLORS.inkMuted} fontSize="14px" maxW="420px">
          Quando a empresa responder questões vinculadas a indicadores GRI, os dados aparecem aqui.
        </Text>
      </Flex>
    );
  }

  return (
    <Box>

      <Box bg={COLORS.surface} borderRadius="12px" p={["20px", "28px"]}>
        <Tabs variant="line" colorScheme="blue" isLazy>
          <TabList overflowX="auto" overflowY="hidden">
            {seriesDisponiveis.map((s) => (
              <Tab key={s} fontWeight={600} whiteSpace="nowrap">
                {rotuloDaSerie(s)} ({bySeries.get(s)?.size ?? 0})
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {seriesDisponiveis.map((s) => {
              const indicators = [...(bySeries.get(s)?.values() || [])];
              return (
                <TabPanel key={s} px={0} pt="20px">
                  <PaginatedIndicatorGrid
                    indicators={indicators}
                    renderCard={(indicator) => {
                      const rows = selectedYears.map((year) => ({
                        ano: String(year),
                        valor: typeof indicator.byYear[year] === "number" ? indicator.byYear[year] : null,
                      }));
                      return (
                        <Box key={indicator.code} border="1px solid #EDF2F7" borderRadius="10px" p="16px">
                          <Text fontSize="13px" fontWeight={600} color={COLORS.ink}>
                            {indicator.code} — {indicator.name}
                          </Text>
                          <Text fontSize="11px" color={COLORS.inkMuted} mb="12px">
                            Unidade: {indicator.unit || "—"}
                          </Text>
                          <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" vertical={false} />
                              <XAxis dataKey="ano" tick={{ fontSize: 11, fill: COLORS.inkMuted }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                              <YAxis tick={{ fontSize: 11, fill: COLORS.inkMuted }} tickLine={false} axisLine={false} />
                              <Tooltip
                                content={(props) => <ChartTooltip {...props} unit={indicator.unit} />}
                                cursor={{ fill: "rgba(0,115,187,0.06)" }}
                              />
                              <Bar dataKey="valor" name={indicator.code} fill={COLORS.brand} radius={[4, 4, 0, 0]} maxBarSize={40} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      );
                    }}
                  />
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}
